import Promise from 'bluebird';
import _ from 'underscore';
import Backbone from 'backbone';
import ko from 'knockout';
import config from '@bingads-webui-component-legacy/config/index';
import { getBulkApiErrors } from '@bingads-webui-component-legacy/api-errors-campaign/index';
import withUiBlock from '@bingads-webui-component-legacy/uiblocker/index';
import jsData from '@bingads-webui-component-legacy/js-data-campaign/index';
import template from '@bingads-webui-campaign-legacy/budget/template/inline-budget-editor.pug';
import ViewModel from '@bingads-webui-campaign-legacy/budget/viewmodel/inline-budget-editor';
import templateWithLandscape from '@bingads-webui-campaign-legacy/budget/template/inline-budget-editor-with-landscape.pug';
import { logBudgetLandScapeView, logBudgetLandScapeAdoption } from '@bingads-webui-campaign-legacy/budget/shared/log';
import load from '@bingads-webui-campaign-legacy/budget/view/dynamic-require-es6-load';
import { MinXandrDailyBudgetRangePerCurrency, LinkedInBudgetRangePerCurrency, getBudgetRangeOld } from '@bingads-webui-campaign-legacy/common-grid-config/budget/get-budget-range';
import { isXandrServing } from '@bingads-webui-campaign-react/constants';

const accountCurrencyCode = config.get('AccountCurrencyCode');
const i18nGlobal = window.globalLegacyI18n.get('component/i18n/loader!legacy/global');
const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');

const BUDGET_SUGGESTION = 'BudgetSuggestions';
const LIFETIME_BUDGET_TYPE = 'LifetimeBudgetStandard';

function getErrors(response) {
  const unexpectedError = [i18nGlobal.getString('ErrorNumber_-1')];

  if (!response || !response.errors) {
    return unexpectedError;
  }

  const errors = getBulkApiErrors(response.errors);

  if (!errors || !errors[0] || !errors[0].message) {
    return unexpectedError;
  }

  return errors;
}

function fetchBudgetLandscape(campaignId, odata) {
  const odataPath = config.get('adinsight.odata.url');
  const uri = `Customers(:customer_id)/Accounts(:account_id)/Campaigns(${campaignId})/Opportunity.GetBudgetLandscape`;

  return odata.get(uri, {
    path: 'AdInsight/V2/',
    host: odataPath,
  });
}

export default class View extends Backbone.View {
  initialize({ item, $elToBlock, isForVNext, permissions, currencySymbol, clientName, perfMarker, showBudgetMinRecommend, importNotificationCallback }) {
    if (!item.Id || !item.Budget) {
      throw new Error('Campaign Id or Budget not found when update budget.');
    }

    this.item = item;
    this.$elToBlock = $elToBlock;
    this.isForVNext = isForVNext;
    this.permissions = permissions;
    this.currencySymbol = currencySymbol;
    this.clientName = clientName;
    this.perfMarker = perfMarker;
    this.landscapeEnabled = permissions.IsBudgetInlineSuggestionEnabled;
    this.importNotificationCallback = importNotificationCallback;

    const budgetRange = getBudgetRangeOld(accountCurrencyCode);
    const { Type: budgetType, Amount, LifetimeBudgetAmount } = item.Budget || {};

    this.budgetAmount = budgetType === LIFETIME_BUDGET_TYPE ? LifetimeBudgetAmount : Amount;

    this.budgetRange = {
      CeilingValue: budgetType === LIFETIME_BUDGET_TYPE ? budgetRange.LifetimeRange.CeilingValue : budgetRange.DailyRange.CeilingValue,
      FloorValue: budgetType === LIFETIME_BUDGET_TYPE ? budgetRange.LifetimeRange.FloorValue : budgetRange.DailyRange.FloorValue,
    };
    const { CampaignType, CampaignSubType, CampaignSettings } = item;

    if (isXandrServing(CampaignSubType, CampaignSettings) && MinXandrDailyBudgetRangePerCurrency[accountCurrencyCode]) {
      this.budgetRange.FloorValue =  MinXandrDailyBudgetRangePerCurrency[accountCurrencyCode];
    }

    if (CampaignType === 'LinkedIn' && LinkedInBudgetRangePerCurrency[accountCurrencyCode]) {
      this.budgetRange.FloorValue = LinkedInBudgetRangePerCurrency[accountCurrencyCode].Min;
      this.budgetRange.CeilingValue = LinkedInBudgetRangePerCurrency[accountCurrencyCode].Max;
    }

    this.viewModel = new ViewModel(
      _.extend(
        _.pick(item.Budget, 'Type', 'CampaignType'),
        { showBudgetMinRecommend, CampaignSubType, CampaignSettings, budgetRange: this.budgetRange, Amount: this.budgetAmount, currencySymbol: this.currencySymbol },
      ));
    const hasBudgetLandscape = _.contains(this.item.DeliveryStatusDetails, BUDGET_SUGGESTION) ||
      this.item.DeliveryStatus === BUDGET_SUGGESTION;

    if (hasBudgetLandscape && this.landscapeEnabled) {
      const budgetSuggestionPerfMarker = this.createMarker('Open_BudgetSuggestion');

      budgetSuggestionPerfMarker('willFetchFile');

      const i18nForBudgetLandscapePromise = load('@bingads-webui/i18n-amd-loader!campaignui/feature/competition-tab');
      const odataPromise = load('@bingads-webui-component-legacy/http/channel/odata');
      const instrumentationPromise = load('@bingads-webui-campaign-legacy/instrumentation/index');
      const budgetLandScapeTablePromise = load('@bingads-webui-campaign/recommendation-inline-budget-view');

      Promise.all([i18nForBudgetLandscapePromise, odataPromise,
        instrumentationPromise, budgetLandScapeTablePromise,
      ]).then(([
        i18nForBudgetLandscape,
        odata,
        instrumentation,
        { BudgetLandScapeTable },
      ]) => {
        budgetSuggestionPerfMarker('willInit');
        this.activity = instrumentation.default.inlineBudgetEditor.create().recordBudgetLandscape.create();

        budgetSuggestionPerfMarker('willFetchData');
        fetchBudgetLandscape(item.Id, odata).then((res) => {
          const rawData = _.chain(res).result('value').first().value();

          if (rawData) {
            budgetSuggestionPerfMarker('willRender');
            this.tableView = new BudgetLandScapeTable({
              opportunityData: rawData,
              i18n: i18nForBudgetLandscape,
              conversionSummaryStatus: rawData.ConversionStatus,
              budgetRange: this.budgetRange,
              currentBudget: this.budgetAmount,
              fromBudgetColumn: true,
              fromCampaignTab: true,
              view: this,
              el: this.$('.landscape-table'),
            });

            this.tableView.render();
            budgetSuggestionPerfMarker('done');

            // landscape view log
            logBudgetLandScapeView(
              rawData.Target && rawData.Target.CampaignId,
              this.clientName,
              rawData.OpportunityId,
              this.tableView.ko_model.rawSuggestedBudget,
              i18nForBudgetLandscape.currency,
              this.activity,
            );
          }
        });
      });
    }
  }

  get events() {
    return {
      'click .save': 'save',
      'click .cancel': 'cancel',
      'click .apply-budget': 'applySuggestedBudget',
    };
  }

  render() {
    if (this.landscapeEnabled) {
      this.$el.html(templateWithLandscape({
        i18nGlobal,
        i18n,
        currencySymbol: this.currencySymbol,
        minimumBudget: this.item.Budget ? (this.budgetAmount * 0.5).toFixed(2) : 'undefined',
        currencySymbolClassName: `symbol-size-${this.currencySymbol.length}`,
      }));
    } else {
      this.$el.html(template({
        i18nGlobal,
        i18n,
      }));
    }

    ko.applyBindings(this.viewModel, this.el);
    return this;
  }

  save() {
    if (!this.viewModel.isValid()) {
      return;
    }

    let updatedBudget = _.defaults(this.viewModel.toObject(), this.item.Budget);

    // update budget by selecting value from landscape data
    if (this.tableView) {
      updatedBudget.Amount = this.tableView.getSelectedNewBudget();
    } else {
      this.viewModel.applySuggestedBudgetLog();
    }
    const updated = _.defaults({
      Budget: updatedBudget,
    }, _.pick(this.item, 'Id'));
    const $elToBlock = this.$elToBlock || this.$el.parent();

    jsData.Campaign
      .update(this.item.Id, updated, { adapter: 'bulk', cacheResponse: false })
      .finally(withUiBlock($elToBlock))
      .then(() => {
        this.trigger('close', updatedBudget);
        if (this.importNotificationCallback) {
          this.importNotificationCallback(updatedBudget);
        }
        if (this.tableView) {
          // landscape adopt log
          logBudgetLandScapeAdoption(
            this.tableView.campaignId,
            this.clientName,
            this.tableView.opportunityId,
            this.tableView.ko_model.rawSuggestedBudget,
            updatedBudget.Amount,
            this.tableView.i18n.currency,
            this.activity,
          );
        }
      })
      .catch((response) => {
        this.viewModel.handleErrors(getErrors(response));
      });
  }

  cancel() {
    this.trigger('close');
  }

  applySuggestedBudget() {
    this.viewModel.applySuggestedBudget();
  }

  createMarker(name) {
    let marker = () => { /* empty because don't have perfmarket in campaign new ui */ };

    if (this.perfMarker) {
      const childMarker = this.perfMarker.createChild(name);

      marker = (event) => {
        childMarker[event]();
      };
    }

    return marker;
  }
}
